<template>
  <button
    type="button"
    class="net-toolbar-button"
    :class="{ '-active': netTools.tool.id === toolKey }"
    :aria-label="$t(`netTools.${toolKey}`)"
    @pointerdown.prevent=""
    @click.prevent="selectTool(toolKey)"
  >
    <slot></slot>
    <div v-if="expandable" class="net-toolbar-button__expansion-icon"></div>
  </button>
</template>

<script setup lang="ts">
import { useNetTools } from "@editor/net/NetTools";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import type { NetToolKey } from "@editor/net/NetTools";

const {
  toolKey,
  expandable = false, // default to false if not provided
} = defineProps<{
  toolKey: NetToolKey;
  expandable?: boolean;
}>();
const emit = defineEmits<{
  (e: "select", key: NetToolKey): void;
}>();

const netTools = useNetTools();
const selection = useSelectionStore();

function selectTool(tool: NetToolKey) {
  netTools.setTool(tool);
  if (netTools.tool.type == "creating") {
    // deselect all items when switching to a creating tool
    selection.clear();
  }
  emit("select", toolKey);
}
</script>

<style lang="scss">
.net-toolbar-button {
  position: relative;
  appearance: none;
  font-family: inherit;
  background-color: transparent;
  width: 100%;
  border: none;
  height: var(--h);
  padding: 1rem 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 18px;
  transition: background-color 100ms, color 200ms;

  &:hover {
    background-color: var(--hover-bg-color);
  }

  &.-active {
    color: var(--color-action);
    background-color: var(--active-bg-color);

    &:hover {
      background-color: var(--active-bg-color-hover);
    }
  }

  &__expansion-icon {
    position: absolute;
    right: 0.25rem;
    bottom: 0.4em;
    // Triangle pointing down-right
    width: 0;
    height: 0;
    border-left: 0.3rem solid transparent;
    border-right: 0.3rem solid transparent;
    border-top: 0.3rem solid var(--color-text-light);
    transform: rotate(-45deg);
  }
}
</style>